import { faArrowDown, faRotate } from '@fortawesome/pro-regular-svg-icons';
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
import { add, formatDistanceStrict, formatDistanceToNowStrict } from 'date-fns';

import Badge from 'components/Badge';

import DashboardCard from '../DashboardCard';
import Grid from '../Grid';
import Leaderboard from './components/Leaderboard/Leaderboard';

import styles from './github-dashboard.module.scss';
import COLOURS from 'styles/export-vars.module.scss';

// --- Regression helpers ------------------------------------------------------

function regress(points: { x: number; y: number }[]) {
  const n = points.length;
  if (n < 2) {
    return { slope: 0, intercept: points[0]?.y ?? 0 };
  }

  const meanX = points.reduce((s, p) => s + p.x, 0) / n;
  const meanY = points.reduce((s, p) => s + p.y, 0) / n;
  const num = points.reduce((s, p) => s + (p.x - meanX) * (p.y - meanY), 0);
  const den = points.reduce((s, p) => s + (p.x - meanX) ** 2, 0) || 1;
  const slope = num / den;
  const intercept = meanY - slope * meanX;
  return { slope, intercept };
}

function buildSeries(raw: { week: string; avgOpenTime: number }[]) {
  // If raw is already in chronological order, keep as-is.
  const data = raw.slice();

  const pts = data.map((d, i) => ({ x: i, y: d.avgOpenTime }));
  const { slope, intercept } = regress(pts);

  // Merge trend back into the SAME objects so both lines share one dataset
  return data.map((d, i) => ({
    week: d.week,
    avgOpenTime: d.avgOpenTime,
    trend: slope * i + intercept
  }));
}

// First→last percent change (clearer for the arrow)
function percentChangeFirstToLast(values: number[]) {
  if (values.length < 2) {
    return 0;
  }
  const first = values[0];
  const last = values[values.length - 1];
  if (!Number.isFinite(first) || first === 0) {
    return 0;
  }
  return ((last - first) / first) * 100;
}

// ---------------------------------------------------------------------------

function convertDecimalHoursToReadableTime(decimalHours: number) {
  const startDate = new Date(0);
  const endDate = add(startDate, { hours: decimalHours });
  return formatDistanceStrict(startDate, endDate);
}

function buildStaleLabel(lastUpdated?: number) {
  if (!lastUpdated) {
    return 'Reconnecting';
  }

  return `Reconnecting - updated ${formatDistanceToNowStrict(lastUpdated)} ago`;
}

function buildOpenReviewsBadge(openReviews: number) {
  if (openReviews <= 5) {
    return <Badge variant="glass" type="SUCCESS" label="Healthy backlog" />;
  }

  if (openReviews <= 10) {
    return <Badge variant="glass" type="WARNING" label="Backlog building" />;
  }

  return <Badge variant="glass" type="DANGER" label="Backlog critical" />;
}

function buildWeeklyHealthBadge(thisWeek: number, baseline: number) {
  if (!thisWeek) {
    return null;
  }

  if (baseline <= 0) {
    return <Badge variant="glass" type="NEUTRAL" label="In line with avg" />;
  }

  const change = (thisWeek - baseline) / baseline;

  if (change < -0.05) {
    return <Badge variant="glass" type="SUCCESS" label="Faster than avg" />;
  }

  if (change > 0.05) {
    return <Badge variant="glass" type="WARNING" label="Slower than avg" />;
  }

  return <Badge variant="glass" type="NEUTRAL" label="In line with avg" />;
}

function buildBaselineBadge() {
  return <Badge variant="glass" type="NEUTRAL" label="4-week baseline" />;
}

function buildTrendBadge(trendPct: number) {
  if (trendPct === 0) {
    return <Badge variant="glass" type="NEUTRAL" label="Flat" />;
  }

  const trendingDown = trendPct < 0;

  return (
    <Badge
      variant="glass"
      type={trendingDown ? 'SUCCESS' : 'DANGER'}
      icon={trendingDown ? faArrowDown : faArrowUp}
      label={`${trendingDown ? 'Trending down' : 'Trending up'} ${Math.abs(trendPct).toFixed(0)}%`}
    />
  );
}

export default function GithubDashboard({
  data,
  stale,
  lastUpdated
}: {
  data: any;
  stale?: boolean;
  lastUpdated?: number;
}) {
  const merged = buildSeries(data.prCloseTimeWeekTrend);
  const trendPct = percentChangeFirstToLast(merged.map((d) => d.avgOpenTime));

  const yMax = Math.ceil(Math.max(...merged.flatMap((d) => [d.avgOpenTime ?? 0, d.trend ?? 0])) * 1.1) || 1;
  const yMin = 0;

  const last4WeeksSubtitle = `Last 4 Weeks  |  ${data.reviewerDistribution.reduce((sum, value) => sum + value, 0)} reviews`;
  const thisWeekAverage = data.prCloseTimeWeekTrend[3].avgOpenTime;
  const bars = data.reviewerDistribution
    .filter((value: number) => value > 1)
    .sort((a: number, b: number) => b - a)
    .map((value: number, index: number) => ({ index, value }));

  return (
    <div className={styles['github-dashboard']}>
      {stale && (
        <Badge
          type="WARNING"
          variant="glass"
          icon={faRotate}
          label={buildStaleLabel(lastUpdated)}
          className={styles['github-dashboard__stale']}
          ariaLabel="Connection status"
        />
      )}
      <Grid>
        <Grid.Item span={1}>
          <DashboardCard.Text
            title="Open Reviews"
            text={`${data.openPrReviews}`}
            subtitle="Currently open non-draft reviews"
            value={data.openPrReviews}
            glow
            badge={buildOpenReviewsBadge(data.openPrReviews)}
            colourThresholds={[
              { min: 6, max: 10, background: 'auroraAmber' },
              { min: 11, background: 'auroraRed' }
            ]}
          />
        </Grid.Item>
        <Grid.Item span={1}>
          <DashboardCard
            title="Reviewer Distribution"
            subtitle={`This Month (${new Date().toLocaleString('default', { month: 'long' })})`}
          >
            {bars.length === 0 ? (
              <p className={styles['github-dashboard__distribution-empty']}>Not enough review activity yet</p>
            ) : (
              <div className={styles['github-dashboard__distribution']}>
                <div className={styles['github-dashboard__chart']}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={bars} margin={{ top: 30, right: 6, bottom: 4, left: 6 }}>
                      <defs>
                        <linearGradient id="auroraBarFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={COLOURS.auroraCyan} />
                          <stop offset="100%" stopColor={COLOURS.auroraIndigo} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="index"
                        tick={false}
                        axisLine={{ stroke: 'rgba(255,255,255,0.12)' }}
                        tickLine={false}
                        height={4}
                      />
                      <YAxis hide />
                      <Bar dataKey="value" fill="url(#auroraBarFill)" radius={[3, 3, 0, 0]} isAnimationActive>
                        <LabelList
                          dataKey="value"
                          position="top"
                          offset={12}
                          fill="#fff"
                          fontSize={20}
                          fontWeight={800}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className={styles['github-dashboard__distribution-captions']}>
                  <span>most active</span>
                  <span>least active</span>
                </div>
              </div>
            )}
          </DashboardCard>
        </Grid.Item>
        <Grid.Item span={1}>
          <DashboardCard.Text
            title="Average Time To Close a PR"
            subtitle="This Week"
            text={!thisWeekAverage ? 'No PRs Closed' : convertDecimalHoursToReadableTime(thisWeekAverage)}
            value={thisWeekAverage}
            badge={buildWeeklyHealthBadge(thisWeekAverage, data.averagePrCloseTime)}
            colourThresholds={[
              { min: 9, max: 24, background: 'auroraAmber' },
              { min: 25, background: 'auroraRed' }
            ]}
          />
        </Grid.Item>
        <Grid.Item span={1}>
          <DashboardCard.Text
            title="Average Time To Close a PR"
            subtitle={last4WeeksSubtitle}
            text={convertDecimalHoursToReadableTime(data.averagePrCloseTime)}
            value={data.averagePrCloseTime}
            badge={buildBaselineBadge()}
            colourThresholds={[
              { min: 9, max: 24, background: 'auroraAmber' },
              { min: 25, background: 'auroraRed' }
            ]}
          />
        </Grid.Item>
        <Grid.Item span={2}>
          <DashboardCard
            title="Trend in PR Open Times"
            subtitle={last4WeeksSubtitle}
            headerAction={buildTrendBadge(trendPct)}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={merged} margin={{ top: 28, right: 18, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="auroraLineFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLOURS.auroraCyan} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={COLOURS.auroraCyan} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="week"
                  padding={{ left: 8, right: 8 }}
                  tick={{ fill: COLOURS.auroraAxis, fontSize: 14 }}
                  axisLine={{ stroke: COLOURS.auroraGrid }}
                  tickLine={false}
                />
                <YAxis
                  domain={[yMin, yMax]}
                  width={44}
                  tick={{ fill: COLOURS.auroraAxis, fontSize: 14 }}
                  axisLine={{ stroke: COLOURS.auroraGrid }}
                  tickLine={false}
                  tickFormatter={(value: number) => `${value}h`}
                />
                <CartesianGrid stroke={COLOURS.auroraGrid} strokeDasharray="5 5" />
                <Area dataKey="avgOpenTime" type="monotone" stroke="none" fill="url(#auroraLineFill)" />
                <Line
                  dataKey="avgOpenTime"
                  stroke={COLOURS.auroraCyan}
                  dot={{ r: 7, fill: COLOURS.auroraCyan, stroke: '#fff', strokeWidth: 2.5 }}
                  strokeWidth={4.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  name="Weekly average"
                  type="monotone"
                >
                  <LabelList
                    content={({ x = 0, y = 0, value, index = 0 }) => {
                      // The end labels lean inwards so they clear the axes
                      const isFirst = index === 0;
                      const isLast = index === merged.length - 1;
                      const anchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
                      return (
                        <text
                          x={Number(x) + (isFirst ? -6 : isLast ? 8 : 0)}
                          y={Number(y) - 20}
                          textAnchor={anchor}
                          fontSize={21}
                          fontWeight={700}
                          fill="#fff"
                          // Halo keeps the value legible where it crosses the trend line
                          stroke="#0d1329"
                          strokeWidth={5}
                          paintOrder="stroke"
                        >
                          {`${Number(value).toFixed(0)}h`}
                        </text>
                      );
                    }}
                  />
                </Line>
                <Line
                  dataKey="trend"
                  type="linear"
                  stroke={trendPct < 0 ? COLOURS.auroraMint : COLOURS.auroraRed}
                  strokeWidth={3.5}
                  strokeDasharray="7 6"
                  // Round dash caps can flash at the wrapped end of the path
                  // before Recharts' draw animation reaches that point.
                  strokeLinecap="butt"
                  dot={false}
                  name="Trend"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </DashboardCard>
        </Grid.Item>
        <Grid.Item span={2}>
          <DashboardCard
            title="Reviewer Leaderboard"
            subtitle={`This Month (${new Date().toLocaleString('default', { month: 'long' })})`}
          >
            <Leaderboard data={data.leaderboard} />
          </DashboardCard>
        </Grid.Item>
      </Grid>
    </div>
  );
}
